import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";

import Constant from "../../Constant";
import BuyUnits from "./Buyunit";
import Apa1 from "../../Assets/images/apa1.png";
import Note from "../../Assets/images/note.svg";
import getServices from "../../Services/get-service";
import postService from "../../Services/post-service";
import ListingSkeleton from "./ListingSkeleton";
import ListingOverviewTab from "./ListingOverviewTab";
import ListingVideoTab from "./ListingVideoTab";
import ListingMapTab from "./ListingMapTab";

const drawerWidth = 490;

function ListingDrawer({
  open,
  handleClose,
  id,
  propertyId: propPropertyId,
  details: rawDetails,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [listingData, setListingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  const activePropertyId =
    propPropertyId || id || rawDetails?.id || rawDetails?.propertyId;

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setIsPlayingVideo(false);
      setVideoError(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !activePropertyId) {
      setListingData(null);
      setLoading(false);
      setIsSaved(false);
      return;
    }

    setLoading(true);
    setListingData(null);

    getServices
      .getListingById(activePropertyId)
      .then((response) => {
        const projects = response?.data?.data?.projects;

        if (response?.data?.statusCode === 200 && Array.isArray(projects)) {
          const matchedProject =
            projects.find(
              (project) => String(project.id) === String(activePropertyId),
            ) ??
            projects[0] ??
            null;

          setListingData(matchedProject);
        } else {
          setListingData(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching listing details:", error);
        setListingData(null);
      })
      .finally(() => {
        setLoading(false);
      });

    getServices
      .getProjectBuyLater(100, 0)
      .then((savedRes) => {
        if (savedRes?.data?.statusCode === 200) {
          const rawData = savedRes.data?.data ?? savedRes.data;
          let allSaved = [];
          if (Array.isArray(rawData)) allSaved = rawData;
          else if (rawData && Array.isArray(rawData.savedProjects))
            allSaved = rawData.savedProjects;
          else if (rawData && Array.isArray(rawData.content))
            allSaved = rawData.content;
          else if (rawData && Array.isArray(rawData.projects))
            allSaved = rawData.projects;
          else if (rawData && Array.isArray(rawData.data))
            allSaved = rawData.data;

          const isMatch = allSaved.some((p) => {
            const idVal =
              p.propertyId ||
              p.projectId ||
              p.id ||
              p.property?.id ||
              p.project?.id;
            return String(idVal) === String(activePropertyId);
          });
          setIsSaved(isMatch);
        }
      })
      .catch((err) => {
        console.error("Error checking bookmark status:", err);
      });
  }, [open, activePropertyId]);

  const handleToggleBookmark = (e) => {
    e?.stopPropagation?.();
    if (bookmarkLoading || !activePropertyId) return;
    setBookmarkLoading(true);

    if (isSaved) {
      postService
        .removeProjectBuyLater(activePropertyId)
        .then((res) => {
          setBookmarkLoading(false);
          if (res?.data?.statusCode === 200) {
            setIsSaved(false);
            toast.success(res?.data?.description, {
              autoClose: 1000,
            });
          } else {
            toast.error(res?.data?.description, {
              autoClose: 1000,
            });
          }
        })
        .catch((err) => {
          setBookmarkLoading(false);
          toast.error(err?.response?.data?.description, { autoClose: 1000 });
        });
    } else {
      postService
        .addProjectBuyLater(activePropertyId)
        .then((res) => {
          setBookmarkLoading(false);
          if (res?.data?.statusCode === 200) {
            setIsSaved(true);
            toast.success(res?.data?.description || "Saved to bookmarks", {
              autoClose: 1500,
            });
          } else {
            toast.error(res?.data?.description || "Failed to bookmark", {
              autoClose: 1500,
            });
          }
        })
        .catch((err) => {
          setBookmarkLoading(false);
          toast.error(
            err?.response?.data?.description || "Failed to bookmark",
            { autoClose: 1500 },
          );
        });
    }
  };

  const projectItem = listingData;
  const title = projectItem?.title || "";
  const rawType = projectItem?.projectType || "";
  const isLand = !rawType || rawType.toLowerCase().includes("land");
  const type = isLand ? "Land" : "Property";

  const location =
    [projectItem?.street, projectItem?.locality].filter(Boolean).join(", ") ||
    "";
  let displayImage = Apa1;
  const apiImages = projectItem?.projectImages;
  if (Array.isArray(apiImages) && apiImages.length > 0) {
    displayImage = apiImages[0]?.imageUrl || apiImages[0]?.image || Apa1;
  } else if (projectItem?.imageUrl) {
    displayImage = projectItem.imageUrl;
  }

  // Price / Per Unit
  const pricePerUnit = Number(projectItem?.unitPrice ?? "0");

  // Area
  const area = projectItem?.coveredAreas
    ? `${projectItem.coveredAreas} sq. m`
    : projectItem?.totalAreas;

  // Description
  const description = projectItem?.description || "";

  // Developer info
  const developerName = projectItem?.developerName || "";

  const developerPhone = projectItem?.phoneNumber || "";

  const unitsLeft = projectItem?.unitRemaining ?? "0";

  const totalUnits = projectItem?.totalUnit ?? "0";

  const fundingProgress = projectItem?.percentageSold ?? "0";

  const targetCapital = Number(projectItem?.price) || "0";

  const raised = Number(projectItem?.amountRaised) || "0";

  const videoTourTitle = "Virtual Property Tour";

  const videoTourDesc =
    "Guided video walkthrough of the property and surroundings";

  const videoLink = projectItem?.videoLink || "";

  const mapDesc = location
    ? `${location}, Well-connected to major infrastructure corridors.`
    : "Well-connected to major infrastructure corridors.";

  const handleShare = () => {};

  const handleOpenFinance = () => {
    setBuyUnitsOpen(true);
  };

  const handleCloseFinance = () => {
    setBuyUnitsOpen(false);
  };

  return (
    <>
      <BuyUnits
        open={buyUnitsOpen}
        handleClose={handleCloseFinance}
        closeDrawer={handleClose}
        number={unitsLeft}
        id={projectItem?.id || activePropertyId}
        amount={pricePerUnit}
        type={isLand ? "land" : "property"}
        details={{ ...(projectItem || {}) }}
      />
      <Drawer
        anchor="right"
        variant="temporary"
        open={open}
        onClose={handleClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: { xs: "100%", sm: drawerWidth },
            backgroundColor: "#FFFFFF",
            boxShadow: "-8px 0 25px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        {loading ? (
          <ListingSkeleton handleClose={handleClose} />
        ) : !projectItem ? (
          <div className="flex flex-col h-full bg-[#FFFFFF] text-[#1E222B]">
            <div className="p-4 flex items-center justify-between border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-base">
                Listing Details
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer transition-colors"
                aria-label="Close drawer"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <img
                src={Note}
                alt="Not found"
                className="w-16 h-16 opacity-40 mb-3"
              />
              <h4 className="text-[#1E222B] font-[700] text-[15px] mb-1">
                Listing Not Available
              </h4>
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 bg-[#00BC67] text-white text-xs font-semibold rounded-xl hover:bg-[#00a85c] transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full bg-[#FFFFFF] text-[#1E222B] relative">
            <div className="flex-1 overflow-y-auto pb-4">
              <div className="relative h-[255px] md:h-[270px] w-full bg-gray-900 select-none overflow-hidden flex-shrink-0">
                <img
                  src={displayImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/85 pointer-events-none" />

                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="w-9 h-9 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-xs flex items-center justify-center text-white cursor-pointer transition-transform active:scale-95 shadow-sm"
                    aria-label="Close drawer"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleToggleBookmark}
                      disabled={bookmarkLoading}
                      className="w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md flex items-center justify-center text-white shadow-sm cursor-pointer transition-transform active:scale-95"
                      aria-label="Bookmark property"
                    >
                      {bookmarkLoading ? (
                        <CircularProgress size={16} sx={{ color: "#00BC67" }} />
                      ) : isSaved ? (
                        <BookmarkIcon sx={{ fontSize: 19, color: "#00BC67" }} />
                      ) : (
                        <BookmarkBorderIcon sx={{ fontSize: 19 }} />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleShare}
                      className="w-9 h-9 rounded-full bg-black/40 hover:bg-black/65 backdrop-blur-md flex items-center justify-center text-white cursor-pointer transition-transform active:scale-95 shadow-sm"
                      aria-label="Share listing"
                    >
                      <ShareOutlinedIcon sx={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 z-10">
                  <span className="inline-block px-2.5 py-0.5 rounded-[4px] bg-[#65BA00] text-white text-[11px] font-bold tracking-wide shadow-xs mb-1.5">
                    {type}
                  </span>
                  <h2 className="text-white font-[800] text-[22px] md:text-[24px] tracking-tight leading-tight drop-shadow-xs">
                    {title}
                  </h2>
                  <div className="flex items-center gap-1 text-white/90 text-[12px] mt-1 drop-shadow-2xs">
                    <PlaceOutlinedIcon
                      sx={{ fontSize: 15, color: "#FFFFFF" }}
                    />
                    <span className="font-[400] truncate">{location}</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#18191B] text-white px-6 py-3.5 flex items-center justify-between select-none">
                <div>
                  <p className="text-[#999FA9] text-[11px] font-[500]">
                    Per Unit
                  </p>
                  <p className="text-white font-[700] text-[15px] mt-0.5">
                    ₦{Constant.FormatNumber(pricePerUnit)}
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-[#999FA9] text-[11px] font-[500]">
                    Remaining
                  </p>
                  <p className="text-white font-[700] text-[15px] mt-0.5">
                    {unitsLeft}/{totalUnits}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-[#999FA9] text-[11px] font-[500]">Area</p>
                  <p className="text-white font-[700] text-[15px] mt-0.5">
                    {area}
                  </p>
                </div>
              </div>
              <div className="flex items-center border-b border-gray-100 bg-white sticky top-0 z-20">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab(0);
                    setIsPlayingVideo(false);
                  }}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${
                    activeTab === 0
                      ? "text-[#1E222B]"
                      : "text-[#808285] hover:text-[#1E222B]"
                  }`}
                >
                  Overview
                  {activeTab === 0 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab(1)}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${
                    activeTab === 1
                      ? "text-[#1E222B]"
                      : "text-[#808285] hover:text-[#1E222B]"
                  }`}
                >
                  Video
                  {activeTab === 1 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveTab(2);
                    setIsPlayingVideo(false);
                  }}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${
                    activeTab === 2
                      ? "text-[#1E222B]"
                      : "text-[#808285] hover:text-[#1E222B]"
                  }`}
                >
                  Map
                  {activeTab === 2 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>
              </div>

              <div className="p-4">
                {activeTab === 0 && (
                  <ListingOverviewTab
                    fundingProgress={fundingProgress}
                    isLand={isLand}
                    pricePerUnit={pricePerUnit}
                    unitsLeft={unitsLeft}
                    totalUnits={totalUnits}
                    area={area}
                    targetCapital={targetCapital}
                    raised={raised}
                    developerName={developerName}
                    developerPhone={developerPhone}
                    description={description}
                  />
                )}

                {activeTab === 1 && (
                  <ListingVideoTab
                    videoLink={videoLink}
                    displayImage={displayImage}
                    isPlayingVideo={isPlayingVideo}
                    setIsPlayingVideo={setIsPlayingVideo}
                    videoError={videoError}
                    setVideoError={setVideoError}
                    videoTourTitle={videoTourTitle}
                    videoTourDesc={videoTourDesc}
                  />
                )}

                {activeTab === 2 && <ListingMapTab mapDesc={mapDesc} />}
              </div>
            </div>
            <div className="p-4 pt-3 bg-white border-t border-gray-100 flex flex-col items-center flex-shrink-0">
              <button
                type="button"
                onClick={handleOpenFinance}
                className="w-full py-3.5 rounded-xl bg-[#00BC67] hover:bg-[#00a85c] text-white font-[700] text-[15px] transition-all duration-200 shadow-xs hover:shadow-md active:scale-[0.99] cursor-pointer text-center"
              >
                Buy Units
              </button>
              <span className="text-[11px] text-[#999FA9] font-[400] mt-2">
                Availability updates in real time
              </span>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}

export default ListingDrawer;
